import { useEffect } from 'react';
import Router from 'next/router';

const DEFAULT_TIMEOUT = 3000;

function useHashObserver(timeout = DEFAULT_TIMEOUT) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let observer = null;
    let timeoutId = null;
    let currentHash = null;
    let isObserving = false;

    const scrollToElement = hash => {
      if (!hash) return false;

      const id = hash.startsWith('#') ? hash.slice(1) : hash;
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView();
        return true;
      }

      return false;
    };

    const stopObserving = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      isObserving = false;
      currentHash = null;
    };

    const startObserving = hash => {
      if (isObserving) {
        stopObserving();
      }

      currentHash = hash;
      isObserving = true;

      if (scrollToElement(hash)) {
        stopObserving();
        return;
      }

      observer = new MutationObserver(() => {
        if (scrollToElement(currentHash)) {
          stopObserving();
        }
      });

      const targetNode =
        document.getElementById('__next') || document.getElementById('root') || document.body;
      observer.observe(targetNode, {
        childList: true,
        subtree: true
      });

      timeoutId = setTimeout(() => {
        stopObserving();
      }, timeout);
    };

    const handleRouteChange = url => {
      const hashIndex = url.indexOf('#');
      if (hashIndex === -1) {
        stopObserving();
        return;
      }

      const hash = url.slice(hashIndex);
      if (hash) {
        startObserving(hash);
      }
    };

    const handleHashChange = url => {
      const hashIndex = url.indexOf('#');
      if (hashIndex === -1) {
        stopObserving();
        return;
      }

      const hash = url.slice(hashIndex);
      if (hash) {
        startObserving(hash);
      } else {
        stopObserving();
      }
    };

    Router.events.on('routeChangeComplete', handleRouteChange);
    Router.events.on('hashChangeComplete', handleHashChange);

    if (window.location.hash) {
      startObserving(window.location.hash);
    }

    return () => {
      stopObserving();
      Router.events.off('routeChangeComplete', handleRouteChange);
      Router.events.off('hashChangeComplete', handleHashChange);
    };
  }, [timeout]);
}

export default useHashObserver;
